import { FC, useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { Button, Card, Divider, RadixTable } from '@features/ui';
import {
  periodOptions,
  groupByOptions,
} from '@features/admin/reports/reportData';
import {
  ReportPeriod,
  ReportGroupBy,
  TReportRefundsByCustomerCountryData,
  TReportRefundsByCustomerCountryParams,
} from '@src/types/reports';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { useReportFilters } from '@hooks/useReportFilters';
import { useReportTableColumns } from '@hooks/useReportTableColumns';
import { useReportChartData, getChartColor } from '@hooks/useReportChartData';
import {
  exportRefundsByCustomerCountry,
  getRefundsByCustomerCountry,
} from '@src/services';

import { ReportFilteringSettings } from '../ReportFilteringSettings';

const dateFormatPayload = 'YYYY-MM-DD';

export const RefundsByCustomerCountry: FC = () => {
  const topScrollRef = useRef<HTMLDivElement | null>(null);
  const bottomScrollRef = useRef<HTMLDivElement | null>(null);
  const [tableScrollWidth, setTableScrollWidth] = useState(0);

  // Data state
  const [data, setData] = useState<TReportRefundsByCustomerCountryData[]>([]);

  // Use custom hooks for filter management
  const filters = useReportFilters({
    defaultPeriod: ReportPeriod.LAST_7_DAYS,
    defaultGroupBy: ReportGroupBy.DAY,
    enableComparisons: true,
  });

  // Use custom hook for table columns generation
  const { columns, displayItems, tableData } =
    useReportTableColumns<TReportRefundsByCustomerCountryData>({
      data,
      selectedItems: [],
      compareToPreviousPeriod: filters.compareToPreviousPeriod,
      compareToSamePeriodPreviousYear: filters.compareToSamePeriodPreviousYear,
      getAccessField: (item) => item.date,
      getMainDataField: (item, key) => item.countries[key] || 0,
      getPreviousPeriodField: (item, key) =>
        item.countries_previous?.[key] || 0,
      getPreviousYearField: (item, key) =>
        item.countries_previous_year?.[key] || 0,
      itemsField: 'countries',
      columnTitleFormatter: (key) => key.toUpperCase(),
      valueFormatter: (_, value) => `$${value.toLocaleString()}`,
      includeTotalsRow: true,
    });

  // Use custom hook for chart data transformation
  const chartData = useReportChartData({
    data,
    displayItems,
    compareToPreviousPeriod: filters.compareToPreviousPeriod,
    compareToSamePeriodPreviousYear: filters.compareToSamePeriodPreviousYear,
    getAccessField: (item) => item.date,
    getMainDataField: (item, key) => item.countries[key] || 0,
    getPreviousPeriodField: (item, key) => item.countries_previous?.[key],
    getPreviousYearField: (item, key) => item.countries_previous_year?.[key],
  });

  const buildPayload = (): TReportRefundsByCustomerCountryParams => {
    return {
      grouping: filters.selectedGroupBy,
      start_date: filters.startDate
        ? dayjs(filters.startDate).format(dateFormatPayload)
        : null,
      end_date: filters.endDate
        ? dayjs(filters.endDate).format(dateFormatPayload)
        : null,
      compare_to_previous_period: filters.compareToPreviousPeriod,
      compare_to_same_period_previous_year:
        filters.compareToSamePeriodPreviousYear,
      countries:
        filters.selectedCountries.length > 0
          ? filters.selectedCountries
          : undefined,
    };
  };

  const handleRunReport = async () => {
    filters.setIsLoading(true);
    try {
      const payload = buildPayload();
      const response = await getRefundsByCustomerCountry(payload);

      setData(response.data);
    } catch (error) {
      console.error('Error fetching report:', error);
    } finally {
      filters.setIsLoading(false);
    }
  };

  const handleExportReport = async () => {
    filters.setIsLoading(true);
    try {
      const payload = buildPayload();
      const response = await exportRefundsByCustomerCountry(payload);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');

      link.href = url;
      link.setAttribute('download', 'refunds_by_customer_country.xlsx');
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting report:', error);
    } finally {
      filters.setIsLoading(false);
    }
  };

  useEffect(() => {
    const bottomScrollEl = bottomScrollRef.current;
    if (!bottomScrollEl) return;

    const syncScrollWidth = () => {
      setTableScrollWidth(bottomScrollEl.scrollWidth);
    };

    syncScrollWidth();

    const resizeObserver = new ResizeObserver(syncScrollWidth);
    resizeObserver.observe(bottomScrollEl);

    const tableContentEl = bottomScrollEl.firstElementChild;
    if (tableContentEl instanceof HTMLElement) {
      resizeObserver.observe(tableContentEl);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [columns, tableData]);

  const syncScroll = (source: HTMLDivElement, target: HTMLDivElement) => {
    if (source.scrollLeft === target.scrollLeft) return;
    target.scrollLeft = source.scrollLeft;
  };

  const handleTopScroll = () => {
    const source = topScrollRef.current;
    const target = bottomScrollRef.current;
    if (!source || !target) return;

    syncScroll(source, target);
  };

  const handleBottomScroll = () => {
    const source = bottomScrollRef.current;
    const target = topScrollRef.current;
    if (!source || !target) return;

    syncScroll(source, target);
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold text-white">
        Refunds By Customer Country Report
      </h1>

      <Card className="p-6">
        <ReportFilteringSettings
          showCountriesFilter
          showGroupByFilter
          showPeriodFilter
          compareToPreviousPeriod={filters.compareToPreviousPeriod}
          compareToSamePeriodPreviousYear={
            filters.compareToSamePeriodPreviousYear
          }
          countryOptions={[]}
          dateRangeValue={filters.valueStart}
          groupByOptions={groupByOptions}
          isLoading={filters.isLoading}
          periodOptions={periodOptions}
          periodRange={filters.periodRange}
          selectedCountries={filters.selectedCountries}
          selectedGroupBy={filters.selectedGroupBy}
          selectedPeriod={filters.selectedPeriod}
          showCompareToPreviousPeriod={filters.showPreviousPeriodCheckbox}
          showCompareToSamePeriodPreviousYear={filters.showPreviousYearCheckbox}
          onCompareToPreviousPeriodChange={filters.setCompareToPreviousPeriod}
          onCompareToSamePeriodPreviousYearChange={
            filters.setCompareToSamePeriodPreviousYear
          }
          onCountriesChange={filters.setSelectedCountries}
          onDateRangeChange={filters.setValueStart}
          onGroupByChange={filters.setSelectedGroupBy}
          onPeriodChange={filters.setSelectedPeriod}
          onRunReport={handleRunReport}
        />
      </Card>

      <Card className="p-6">
        <div className="space-y-4">
          <ResponsiveContainer height={400} width="100%">
            <LineChart data={chartData}>
              <CartesianGrid stroke="#333" strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                stroke="#999"
                style={{ fontSize: '12px' }}
              />
              <YAxis
                stroke="#999"
                style={{ fontSize: '12px' }}
                tickFormatter={(value) => `$${value.toLocaleString()}`}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1a1a1a',
                  border: '1px solid #3D3D3D',
                  borderRadius: '8px',
                  boxShadow: '0px 7px 13px 0px #B6D72C14',
                }}
                formatter={(value: number) => `$${value.toLocaleString()}`}
              />
              {displayItems.map((country, index) => (
                <Line
                  key={country}
                  activeDot={{ r: 6 }}
                  dataKey={country}
                  dot={{ r: 4 }}
                  stroke={getChartColor(index)}
                  strokeWidth={2}
                  type="monotone"
                />
              ))}
              {filters.compareToPreviousPeriod &&
                displayItems.map((country, index) => (
                  <Line
                    key={`${country}_previous_period`}
                    dataKey={`${country}_previous_period`}
                    dot={{ r: 3 }}
                    stroke={getChartColor(index)}
                    strokeDasharray="5 5"
                    strokeWidth={2}
                    type="monotone"
                  />
                ))}
              {filters.compareToSamePeriodPreviousYear &&
                displayItems.map((country, index) => (
                  <Line
                    key={`${country}_previous_year`}
                    dataKey={`${country}_previous_year`}
                    dot={{ r: 3 }}
                    stroke={getChartColor(index)}
                    strokeDasharray="2 2"
                    strokeWidth={2}
                    type="monotone"
                  />
                ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card className="p-6">
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-white">Detailed Data</h2>
          <div
            ref={topScrollRef}
            className="overflow-x-auto overflow-y-hidden"
            onScroll={handleTopScroll}
          >
            <div style={{ width: `${tableScrollWidth}px`, height: '1px' }} />
          </div>
          <div
            ref={bottomScrollRef}
            className="overflow-x-auto"
            onScroll={handleBottomScroll}
          >
            <RadixTable columns={columns} data={tableData} />
          </div>
          <Divider className="my-4" />
          <div className="flex justify-end">
            <Button
              className="text-[#B6D72C] border-[#B6D72C] hover:bg-[#B6D72C] hover:text-black"
              disabled={filters.isLoading}
              variant="outline"
              onClick={handleExportReport}
            >
              Download CSV
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};
